import React, { useCallback, useMemo, useRef, useState } from 'react';
import { cloneDeep, isEqual } from 'lodash';

import {
	DialogueAct,
	Skeleton,
	Search,
	Title,
	Button,
	Space,
	IconButton,
} from '@components';

import {
	DeleteDialogueActModal,
	DialogueActModal,
} from '@modals';

import {
	ConvertedDialogueAct,
	useModal,
} from '@common';

import './DialogueActsList.scss';
import { DownloadIcon } from '@icons';

interface DialogueActsListProps {
	onUpdateDialogueActsList: (updatedDialogueActsList: ConvertedDialogueAct[]) => void,
	onPlayDAClick: (startTimeSeconds: number, endTimeSeconds: number) => void,
	setNeedScroll: (s: boolean) => void,
	data: ConvertedDialogueAct[],
	canEditDialogueAct: boolean,
	onPauseDAClick: () => void,
	needScroll: boolean,
	isPlaying: boolean,
	canEditResume: boolean,
	downloadTranscriptAction: () => void,
	downloadTranscriptJsonAction: () => void,
	accessToken: string,
	isDownloadingTranscript: boolean,
	isDownloadingTranscriptJson: boolean,
	isProcessing: boolean,
	isDeveloper: boolean,
}

function DialogueActsList(props: DialogueActsListProps): JSX.Element {
	const {
		onUpdateDialogueActsList,
		canEditDialogueAct,
		onPauseDAClick,
		onPlayDAClick,
		// setNeedScroll,
		needScroll,
		isPlaying,
		data,
		canEditResume,
		accessToken,
		isDownloadingTranscript,
		downloadTranscriptAction,
		isProcessing,
		isDeveloper,
	} = props;

	const
		[selectedDialogueAct, setSelectedDialogueAct] = useState<ConvertedDialogueAct>(null),
		[searchQuery, setSearchQuery] = useState<string>(""),
		dialogueActsListRef = useRef<HTMLDivElement>(null);

	const [partialDAHidden, setPartialDAHidden] = useState<boolean>(true);

	const {
		isModalOpen: isUpdateDialogueActModalOpen,
		closeModal: closeUpdateDialogueActModal,
		openModal: openUpdateDialogueActModal,
	} = useModal();

	const {
		isModalOpen: isDeleteDialogueActModalOpen,
		closeModal: closeDeleteDialogueActModal,
		openModal: openDeleteDialogueActModal,
	} = useModal();

	const onOpenUpdateDialogueActModal = useCallback((dialogueAct: ConvertedDialogueAct): void => {
		setSelectedDialogueAct(dialogueAct);
		openUpdateDialogueActModal();
	}, [setSelectedDialogueAct, openUpdateDialogueActModal]);

	const onOpenDeleteDialogueActModal = useCallback((dialogueAct: ConvertedDialogueAct): void => {
		setSelectedDialogueAct(dialogueAct);
		openDeleteDialogueActModal();
	}, [setSelectedDialogueAct, openDeleteDialogueActModal]);

	const onChangeDialogueAct = useCallback((updatedDialogueAct: ConvertedDialogueAct): void => {
		// LATER: спрятать всю эту логику внутрь модалки
		const newTranscriptRows = data.map((dialogueAct) => {
			return isEqual(dialogueAct.initialData, updatedDialogueAct.initialData) 
				? updatedDialogueAct : dialogueAct;
		});

		onUpdateDialogueActsList(newTranscriptRows);
		setSelectedDialogueAct(null);
	}, [setSelectedDialogueAct, onUpdateDialogueActsList, data]);

	const onDeleteDialogueAct = useCallback((dialogueActToDelete: ConvertedDialogueAct): void => {
		const newTranscriptRows = data.filter((dialogueAct) => {
			return !isEqual(dialogueAct.initialData, dialogueActToDelete.initialData);
		});

		onUpdateDialogueActsList(newTranscriptRows);
		setSelectedDialogueAct(null);
	}, [setSelectedDialogueAct, onUpdateDialogueActsList, data]);

	const updateDialogueActModalView = useMemo((): JSX.Element => {
		return isUpdateDialogueActModalOpen && (
			<DialogueActModal
				closeModal={closeUpdateDialogueActModal}
				dialogueAct={selectedDialogueAct}
				onSubmit={onChangeDialogueAct}
				accessToken={accessToken}
			/>
		);
	}, [isUpdateDialogueActModalOpen, closeUpdateDialogueActModal, accessToken, onChangeDialogueAct, selectedDialogueAct]);

	const deleteDialogueActModalView = useMemo((): JSX.Element => {
		return isDeleteDialogueActModalOpen && (
			<DeleteDialogueActModal
				closeModal={closeDeleteDialogueActModal}
				dialogueAct={selectedDialogueAct}
				onSubmit={onDeleteDialogueAct}
			/>
		);
	}, [isDeleteDialogueActModalOpen, selectedDialogueAct, closeDeleteDialogueActModal, onDeleteDialogueAct]);

	const dialogueActsView = useMemo(() => {
		
		if (!data) return null;
		
		let filteredData = data;
		if (searchQuery) {
			const participantRe = /@[\wа-яА-Я]+/gi;
			const spaceRe = /\s/g;

			const tokens = searchQuery.split(spaceRe);
			const participantTokens = tokens.filter(t => t.match(participantRe));
			const textTokens = tokens.filter(t => !participantTokens.includes(t));

			let participantRes: RegExp[] = null;
			if (participantTokens.length) {
				const participantNames = participantTokens.map(pt => pt.substr(1).replace("_", " "));
				participantRes = participantNames.map(pn => new RegExp(pn, "gi"));

				filteredData = filteredData
					.filter(da => participantRes.some(pt => da.data.name.match(pt)));

				filteredData
					.forEach(da => da.data.nameRes = participantRes);
			}

			let textRe: RegExp = null;
			if (textTokens.length) {
				textRe = new RegExp(textTokens.join(".*"), "gi");
				filteredData = filteredData
					.filter(da => da.data.text.match(textRe));
			}

			filteredData = cloneDeep(filteredData);
			filteredData
					.forEach(da => {
						da.data.textRe = textRe;
						da.data.nameRes = participantRes;
					});
		} else {
			filteredData = cloneDeep(filteredData);
			filteredData
					.forEach(da => {
						da.data.textRe = null;
						da.data.nameRes = null;
					});
		}

		return filteredData
			.map((dialogueAct, i) =>
			<DialogueAct
				onOpenDeleteDialogueActModal={onOpenDeleteDialogueActModal}
				onOpenUpdateDialogueActModal={onOpenUpdateDialogueActModal}
				canEditDialogueAct={canEditDialogueAct}
				onPauseDAClick={onPauseDAClick}
				onPlayDAClick={onPlayDAClick}
				needScroll={needScroll}
				isPlaying={isPlaying}
				data={dialogueAct}
				key={i}
				canEditResume={canEditResume}
			/>
		)
	}, [data, canEditDialogueAct, canEditResume, isPlaying, needScroll, searchQuery,
		onOpenDeleteDialogueActModal, onOpenUpdateDialogueActModal, onPauseDAClick, onPlayDAClick]);

	if (!data) {
		return (
			<div className="dialogue-acts">
				<Title theme="primary">
					Стенограмма
				</Title>
				<Skeleton content="Загрузка..." />
			</div>
		);
	} else if (!data.length && partialDAHidden) {
		return (
			<div className="dialogue-acts">
				<Title theme="primary">
					Стенограмма
				</Title>
				<div className="dialogue-acts__message">{isProcessing ? "Идёт распознавание речи..." : "Аудиозапись не содержит речь, либо её не удалось распознать."}</div>
			</div>
		);
	}

	if (isProcessing && partialDAHidden) {
		return (
			<div className="dialogue-acts">
				<Title theme="primary">
					Стенограмма
				</Title>
				<div className="dialogue-acts__message">Идёт распознавание речи...</div>
					{isDeveloper && data.length ? 
					<div className="dialogue-acts__message">
						<Button onClick={() => setPartialDAHidden(false)}>Показать частичное распознавание</Button>
					</div> 
					: null}
				
			</div>
		);
	}

	return (
		<div className="dialogue-acts">
			<Title theme="primary" trailing={
				<Space className='action-buttons'>
				{/* {isDeveloper && <Button 
					onClick={downloadTranscriptJsonAction}
					isBusy={isDownloadingTranscriptJson}
				>
					Скачать JSON
				</Button>} */}
				<IconButton
					theme="ghost"
					// disabled={!canDownloadSummary}
					onClick={downloadTranscriptAction}
					isBusy={isDownloadingTranscript}
					title="Скачать стенограмму"
					iconSize='24px'
					icon={<DownloadIcon />}
					/>
				</Space>
			}>
				Стенограмма
			</Title>
			<div className="dialogue-acts__searchbox">
				<Search
					placeholder="Поиск по стенограмме"
					className="dialogue-acts__search"
					onChangeSearch={(s) => setSearchQuery(s)}
					defaultValue=""
				/>
			</div>
			<div className="dialogue-acts__list" ref={dialogueActsListRef}>
				{dialogueActsView}
			</div>
			{updateDialogueActModalView}
			{deleteDialogueActModalView}
		</div>
	);
}

export {DialogueActsList};